import { Contact } from '@core/models/admin/contacts.types';
import { CommentSentiment, MediaType } from '@core/models/ApiFields/ig-field-values.types';
import { AIAgent } from '@features/ai-agents/ai-agent.types';
import { PostsChannels } from '@shared/types/channels.types';
import { SocialComment } from './comment.types';

/**
 * Backend model: SocialMediaBasicView
 */
export type PostDTO = {
    caption_sentiment: CommentSentiment;
    caption: string;
    channel: PostsChannels;
    closed: boolean;
    comments_dm_handled: number;
    comments_handled: number;
    comments_public_handled: number;
    comments_unread: number;
    comments: number;
    contact?: Contact;
    content_type: string;
    department_id?: number;
    engagement_rate: number;
    id: number;
    likes?: number;
    media_id: string;
    media_type: MediaType;
    negative_comments: number;
    permalink: string;
    positive_comments: number;
    publish_date: string;
    resources: PostResource[];
    total_reactions?: number;
    updated_at: string;
    user_id?: number;
    ai_agent_id?: AIAgent['id'];
    read?: boolean;

    /**
     * username is always present. same value as contact.ig_username || contact.fb_username if contact is provided
     */
    username: string;

    //    UNUSED FIELDS
    //    avg_watch_time: number;
    //    business_comments: number;
    //    fb_page_id: string;
    //    followers: number;
    //    ig_account: string;
    //    impressions: number;
    //    navigation_swipe_forward: number;
    //    navigation_tap_back: number;
    //    navigation_tap_exit: number;
    //    navigation_tap_forward: number;
    //    plays: number;
    //    product_similarities: unknown[]; // Placeholder for [...], define type if known
    //    profile_activity_bio_link_clicked: number;
    //    profile_activity_call: number;
    //    profile_activity_direction: number;
    //    profile_activity_email: number;
    //    profile_activity_other: number;
    //    profile_activity_text: number;
    //    profile_views: number;
    //    reach: number;
    //    saved: number;
    //    shares: number;
    //    total_interactions: number;
    //    user_comments: number;
    //     view_total_time: number;
};

export type PostResource = {
    image?: string;
    video?: string;
    main: number;
    media_id: string;
};

export class Post implements PostDTO {
    caption_sentiment: CommentSentiment;
    caption: string;
    channel: PostsChannels;
    closed: boolean;
    /** Comments handled by user sending final customer a DM */
    comments_dm_handled: number;
    /** Comments handled by user replying to comment  */
    comments_public_handled: number;
    /** Comments handled by user either replying to comment or via DM */
    comments_handled: number;
    comments_unread: number;
    comments: number;
    contact?: Contact;
    content_type: string;
    department_id?: number;
    engagement_rate: number;
    id: number;
    likes?: number;
    media_id: string;
    media_type: MediaType;
    negative_comments: number;
    permalink: string;
    positive_comments: number;
    publish_date: string;
    resources: PostResource[];
    total_reactions?: number;
    updated_at: string;
    user_id?: number;
    username: string;
    ai_agent_id?: AIAgent['id'];
    read?: boolean;

    positiveRatio: number;
    negativeRatio: number;

    constructor({
        caption_sentiment,
        caption,
        channel,
        closed,
        comments_dm_handled,
        comments_handled,
        comments_public_handled,
        comments_unread,
        comments,
        contact,
        content_type,
        department_id,
        engagement_rate,
        id,
        likes,
        media_id,
        media_type,
        negative_comments,
        permalink,
        positive_comments,
        publish_date,
        resources,
        total_reactions,
        updated_at,
        user_id,
        username,
        read,
        ai_agent_id,
    }: PostDTO) {
        this.caption = caption;
        this.caption_sentiment = caption_sentiment;
        this.channel = channel;
        this.closed = closed;
        this.comments = comments;
        this.comments_dm_handled = comments_dm_handled;
        this.comments_public_handled = comments_public_handled;
        this.comments_handled = comments_handled;
        this.comments_unread = comments_unread;
        this.contact = contact;
        this.content_type = content_type;
        this.department_id = department_id;
        this.engagement_rate = engagement_rate;
        this.id = id;
        this.likes = likes;
        this.media_id = media_id;
        this.media_type = media_type;
        this.negative_comments = negative_comments;
        this.permalink = permalink;
        this.positive_comments = positive_comments;
        this.publish_date = publish_date;
        this.resources = resources;
        this.updated_at = updated_at;
        this.user_id = user_id;
        this.username = username;
        this.total_reactions = total_reactions;
        this.read = read;
        this.ai_agent_id = ai_agent_id;

        this.positiveRatio = positive_comments / (positive_comments + negative_comments);
        this.negativeRatio = negative_comments / (positive_comments + negative_comments);
    }
}

/* UI */

export const REPLY_MODE = {
    comment: 'comment',
    reply: 'reply',
    dm: 'dm',
} as const;
export type ReplyMode = (typeof REPLY_MODE)[keyof typeof REPLY_MODE];

export type PostResponsePayload = {
    id: Post['id'] | SocialComment['id'];
    text: string;
    tmp_id: string; // UUID
};
